import { Constants, isUkcRallyShow, ukcRallyClassLabel } from "@eagerdog/constants";
import { IEvent, IRunningOrderDogClass, IShow } from "@eagerdog/interfaces";

const CIRCLED = ["", "①", "②", "③", "④"];

export function formatMinutes(minutes: number): string {
  if (!minutes) {
    return "0h 0m (0.0m)";
  }
  const hours = Math.floor(minutes / 60);
  const remaining = Math.floor(minutes % 60);
  return `${hours}h ${remaining}m (${minutes.toFixed(1)}m)`;
}

export const GAP_BREAK_NAME = "Gap";

export function isBreak(dogClass: IRunningOrderDogClass): boolean {
  return dogClass.type === Constants.running_order_dog_class_type.break;
}

/** Idle time created by dragging a class later. Hidden on the timeline; it only shifts the clock. */
export function isGapBreak(dogClass: IRunningOrderDogClass): boolean {
  return isBreak(dogClass) && dogClass.name === GAP_BREAK_NAME;
}

export function isJuniorShowmanship(dogClass: IRunningOrderDogClass): boolean {
  return dogClass.show_element === Constants.dog_class_element.junior_showmanship;
}

/** Same label rules as JudgeSchedulesTab.getLevelName, plus split and alpha badges. */
export function classLabel(event: IEvent, show: IShow, dogClass: IRunningOrderDogClass, childLevels: string[] = []): string {
  if (isBreak(dogClass)) {
    return dogClass.name?.trim() || "Break";
  }

  let baseName: string;
  const isAbiSdg = event.sanctioning_club === Constants.sanctioning_club.ABI &&
    show.show_type === Constants.show_type.scent_dog_games;

  if (isAbiSdg) {
    const gameName = dogClass.level || dogClass.show_element || "";
    baseName = dogClass.section ? `${gameName} - ${dogClass.section}` : gameName;
  } else {
    const isAkcHerding = event.sanctioning_club === Constants.sanctioning_club.AKC && [
      Constants.show_type.herding,
      Constants.show_type.pre_trial_herding,
      Constants.show_type.instinct_test_herding,
    ].includes(show.show_type);

    if ([
      Constants.show_type.scentwork,
      Constants.show_type.nosework,
      Constants.show_type.agility,
      Constants.show_type.scent,
      Constants.show_type.urban_rat_race,
      Constants.show_type.cwags_games,
    ].includes(show.show_type)) {
      baseName = [dogClass.show_element, dogClass.level].filter(Boolean).join(" - ");
    } else if (isAkcHerding) {
      baseName = (dogClass.show_element && dogClass.show_element !== show.show_type)
        ? [dogClass.show_element, dogClass.level].filter(Boolean).join(" - ")
        : (dogClass.level || dogClass.show_element || "");
    } else if (isUkcRallyShow(event.sanctioning_club, show.show_type)) {
      // "RO1 B" whether stored as level "RO1 B" or as RO1 + section B
      baseName = ukcRallyClassLabel(dogClass.level, dogClass.section) || dogClass.show_element || "";
    } else {
      baseName = dogClass.level || dogClass.show_element || "";
    }

    const showSection = (
      (event.sanctioning_club === Constants.sanctioning_club.C_WAGS &&
        (show.show_type === Constants.show_type.obedience || show.show_type === Constants.show_type.rally_obedience)) ||
      isAkcHerding ||
      (event.sanctioning_club === Constants.sanctioning_club.FH && show.show_type === Constants.show_type.agility)
    );
    if (showSection && dogClass.section) {
      baseName = `${baseName} - ${dogClass.section}`;
    }
  }

  if (dogClass.variation) {
    baseName = `${baseName} (${dogClass.variation})`;
  }
  if (dogClass.sex_split) {
    baseName = `${baseName} - ${dogClass.sex_split}`;
  }
  if (dogClass.start_letter && dogClass.end_letter) {
    baseName = `${baseName} ${dogClass.start_letter}–${dogClass.end_letter}`;
  }
  if (childLevels.length > 0) {
    baseName = [baseName, ...childLevels].filter(Boolean).join("/");
  }
  if (dogClass.split_number && dogClass.split_total) {
    const badge = CIRCLED[dogClass.split_number] || `${dogClass.split_number}`;
    baseName = `${baseName} ${badge}/${dogClass.split_total}`;
  }
  return baseName;
}

export function childLevelsOf(parent: IRunningOrderDogClass, siblings: IRunningOrderDogClass[]): string[] {
  const parentNew = `${parent.show_element}:${parent.level}`;
  return siblings
    .filter((child) => child.group_with === parentNew || (child.group_with === parent.level && child.show_element === parent.show_element))
    .map((child) => child.level || child.show_element || "")
    .filter(Boolean);
}

export function classTooltip(event: IEvent, show: IShow, dogClass: IRunningOrderDogClass, estStart: string, childLevels: string[] = []): string {
  if (isBreak(dogClass)) {
    return `${dogClass.name || "Break"} · ${dogClass.duration || 0} min · ${estStart}`;
  }
  const lines = [
    classLabel(event, show, dogClass, childLevels),
    dogClass.entries != null ? `${dogClass.entries} entries` : "",
    dogClass.total_minutes != null ? `${dogClass.total_minutes.toFixed(1)} min` : "",
    dogClass.duration != null ? `${dogClass.duration.toFixed(2)} min/entry` : "",
    estStart ? `Est. start ${estStart}` : "",
    dogClass.time_limit && !dogClass.time_limit_search_1 ? `Time limit ${dogClass.time_limit}` : "",
    dogClass.num_hides && !dogClass.num_hides_search_1 ? `Hides ${dogClass.num_hides}` : "",
    dogClass.time_limit_search_1 ? `Search 1 ${dogClass.time_limit_search_1}` : "",
    dogClass.time_limit_search_2 ? `Search 2 ${dogClass.time_limit_search_2}` : "",
    dogClass.time_limit_search_3 ? `Search 3 ${dogClass.time_limit_search_3}` : "",
  ];
  return lines.filter(Boolean).join(" · ");
}
